import cn from 'classnames'
import React, { useMemo } from 'react'
import { useTranslate } from '../src/hooks/useTranslate'
import { CurrentLink } from './CurrentLink'

interface PaginationProps {
	currentPage: number
	isLoading?: boolean
	pagesCount: number
	LinkComponent?: React.ComponentType<{
		page: number
		children: React.ReactElement
	}>
}

const VISIBLE_NEIGHBOURS_OFFSET = 5

export const getPageQueryParams = (page: number) =>
	page === 1 ? { page: undefined } : { page }

const PageLink: React.FC<{ page: number }> = ({ page, children }) => {
	const query = useMemo(() => getPageQueryParams(page), [page])
	return <CurrentLink query={query}>{children}</CurrentLink>
}

export const Pagination: React.FunctionComponent<PaginationProps> = (props) => {
	/* eslint-disable */
	let { currentPage, pagesCount } = props

	if(currentPage < 1 || typeof(currentPage) !== 'number')
	{
		currentPage = 1;
	}
	/* eslint-enable */
	const pagesWithEllipsis = React.useMemo(() => {
		const pages = [...Array(pagesCount)]
			.map((_, i) => i + 1)
			.filter((page) => {
				const isInHotSpot = (page: number) => {
					if (page === 1 || page === pagesCount) {
						return true
					}
					if (
						page >= currentPage - VISIBLE_NEIGHBOURS_OFFSET &&
						page <= currentPage + VISIBLE_NEIGHBOURS_OFFSET
					) {
						return true
					}
					return false
				}
				const isInSmallGap = (page: number) => {
					return isInHotSpot(page - 1) && isInHotSpot(page + 1)
				}
				return isInHotSpot(page) || isInSmallGap(page)
			})

		const pagesWithEllipsis: Array<'ellipsis' | number> = []
		let lastPageNumber = 0
		pages.forEach((page) => {
			if (page !== lastPageNumber + 1) {
				pagesWithEllipsis.push('ellipsis')
			}
			lastPageNumber = page
			pagesWithEllipsis.push(page)
		})
		return pagesWithEllipsis
	}, [currentPage, pagesCount])

	const translate = useTranslate();
	const prevPage = currentPage <= 1 ? undefined : currentPage - 1
	const nextPage = currentPage >= pagesCount ? undefined : currentPage + 1
	const LinkComponent = props.LinkComponent || PageLink

	if (props.pagesCount <= 1) {
		return null
	}

	return (
		<nav className={cn('pagination', props.isLoading && 'is-loading')}>
			{prevPage && (
				<LinkComponent page={prevPage}>
					<a className="pagination-previous" aria-label={translate('previous')} />
				</LinkComponent>
			)}
			<div className="pagination-pages">
				{pagesWithEllipsis.map((page, i) => (
					<React.Fragment key={i}>
						{page === 'ellipsis' ? (
							<span className="pagination-ellipsis">…</span>
						) : (
							<LinkComponent page={page}>
								<a
									className={cn(
										'pagination-page',
										page === currentPage && 'is-current',
									)}
								>
									{page}
								</a>
							</LinkComponent>
						)}{' '}
					</React.Fragment>
				))}
			</div>
			{nextPage && (
				<LinkComponent page={nextPage}>
					<a className="pagination-next" aria-label={translate('next')} />
				</LinkComponent>
			)}
		</nav>
	)
}
